import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import useNotification from 'containers/App/Notifications/useNotification';
import removeSeanceClassroomFromSeances from 'queries/edt/removeSeanceClassroomFromSeances';
import { format } from 'utils/time/format';
import { CJEvent } from 'types/event';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import { useAtom } from 'jotai';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

interface Props {
  seance: CJEvent;
  classroomId?: string;
}

export function useDeleteCJSeance(disableUndoRedo: boolean = false) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}`;
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { t } = useTranslation(['cahierJournal']);
  const { mutateAsync } = useMutation({
    mutationFn: ({ seance, classroomId }: Props) =>
      classroomId
        ? request(
            `${requestURL}/classrooms/${classroomId}/cahier-journal/seances/${seance.id}`,
            {
              method: 'DELETE',
            },
          )
        : request(`${requestURL}/cahier-journal/seances/${seance.id}`, {
            method: 'DELETE',
          }),
    onMutate: ({ seance, classroomId }: Props) => {
      const previousDayData = queryClient.getQueryData<CJEvent[]>([
        schoolyear.id,
        school.id,
        EVENTS_KEY,
        format(seance.start, 'fr-FR'),
      ]);
      if (!disableUndoRedo) {
        const previousEvent = (previousDayData || []).find(
          evt => evt.id === seance.id,
        );
        // TODO TO FIX
        if (classroomId && false) {
          addToPast({
            payload: [previousEvent as CJEvent, seance],
            action: 'UPDATE',
          });
        } else {
          addToPast({
            payload: [seance],
            action: 'DELETE',
          });
        }
      }
      queryClient.setQueryData<CJEvent[]>(
        [schoolyear.id, school.id, EVENTS_KEY, format(seance.start, 'fr-FR')],
        old =>
          classroomId
            ? (removeSeanceClassroomFromSeances(
                old || [],
                seance.id,
                classroomId,
              ) as CJEvent[])
            : (old || []).filter(e => e.id !== seance.id),
      );
      return { previousDayData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          schoolyear.id,
          school.id,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });
    },
    onError: (err: AxiosError, { seance }, context) => {
      queryClient.setQueryData(
        [schoolyear.id, school.id, EVENTS_KEY, format(seance.start, 'fr-FR')],
        context?.previousDayData,
      );
      if (err?.response?.status === 403) {
        notifyError({
          title: t('cahierJournal:messages.unauthorized'),
        });
        return;
      }
      notifyError({
        title: t('cahierJournal:messages.deleteError_one'),
      });
    },
  });
  return mutateAsync;
}
